// Places a position: fixed panel against an anchor element.
//
// Why fixed: an absolutely positioned panel is clipped by any ancestor with
// overflow: hidden, and flipping its direction does not help against a clip.
// Fixed takes it out of that clipping context entirely.
//
// The catch: `fixed` resolves against the nearest ancestor that has a transform,
// not the viewport. bootstrap-sass 3.4 sets `transform: translate(0, 0)` on
// `.modal.in .modal-dialog`, so inside a modal the coordinates are relative to the
// dialog. Rather than sniffing for transformed ancestors, this writes the desired
// viewport coordinates, measures where the panel actually landed, and corrects by
// the difference — which is right whatever the containing block turns out to be.
//
// Lives outside controllers/helpers on purpose: packs/helpers.js runs
// definitionsFromContext over that directory and expects every module there to
// default-export a Stimulus controller.

const EDGE_MARGIN = 16
const ANCHOR_GAP = 4

const OPPOSITE = {top: "bottom", bottom: "top", left: "right", right: "left"}
const VERTICAL_SIDES = {top: true, bottom: true}
const ALIGNS_FOR = {vertical: {left: true, right: true}, horizontal: {top: true, bottom: true}}

// placement is `side`, `side-align`, and optionally the word `auto`:
//
//   pinned                 falls back
//   bottom-left            bottom-left auto
//   bottom-right           bottom-right auto
//   top-left               top-left auto
//   top-right              top-right auto
//   left-top   right-top   left-top auto     right-top auto
//   left-bottom right-bottom …
//
// `side` is which way the panel sits from the anchor. `align` names the anchor edge the
// panel lines its matching edge up with, so `bottom-left` sits below with both left
// edges flush (extending right) and `bottom-right` extends left instead.
//
// Without `auto` the side and alignment are honoured as asked. With `auto` each axis
// falls back on its own, which is what makes a combination behave the way you would
// want: `bottom-left auto` with no room below becomes `top-left`, and if there is then
// no room to the right of the anchor it becomes `top-right`. An axis only flips when
// the opposite genuinely has more room.
//
// Either way the panel is clamped inside the viewport — pinned means "do not switch
// sides", not "run off the screen".
//
// The default is `bottom auto`, which is exactly what every caller got before
// placement existed.
export function placeFloatingPanel(panel, anchor, options = {}) {
  const {
    gap = ANCHOR_GAP,
    margin = EDGE_MARGIN,
    matchWidth = false,
    placement = "bottom auto",
    boundary = null
  } = options

  // Width first: the panel's height depends on it once labels wrap.
  if (matchWidth) panel.style.width = `${anchor.width}px`

  const limit = toRect(boundary)
  const box = {width: panel.offsetWidth, height: panel.offsetHeight, gap, margin, limit}
  const wanted = parsePlacement(placement)

  const side = wanted.auto ? resolveSide(wanted.side, anchor, box) : wanted.side
  const align = wanted.auto ? resolveAlign(side, wanted.align, anchor, box) : wanted.align

  const {top, left} = VERTICAL_SIDES[side]
    ? vertical(side, align, anchor, box)
    : horizontal(side, align, anchor, box)

  panel.style.top = `${top}px`
  panel.style.left = `${left}px`

  // Correct for a transformed containing block (see note above).
  const actual = panel.getBoundingClientRect()
  const dy = top - actual.top
  const dx = left - actual.left

  if (dx !== 0 || dy !== 0) {
    panel.style.top = `${top + dy}px`
    panel.style.left = `${left + dx}px`
  }
}

// Tokens split on spaces or hyphens, so 'bottom-left auto', 'bottom left auto' and
// 'bottom-left-auto' all read the same. Anything unrecognised falls to the defaults
// rather than throwing — a typo in a data attribute should not blank out a panel.
// The box the panel has to stay inside. Defaults to the viewport; pass an element (or
// a rect) to flip and clamp against a container instead — the anchor is still the
// trigger, this only changes what counts as "no room".
function toRect(boundary) {
  if (!boundary) {
    return {top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight}
  }

  const rect = typeof boundary.getBoundingClientRect === "function"
    ? boundary.getBoundingClientRect()
    : boundary

  return {top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom}
}

function parsePlacement(placement) {
  const tokens = String(placement).trim().toLowerCase().split(/[\s-]+/)
  const auto = tokens.includes("auto")
  const [rawSide, rawAlign] = tokens.filter((token) => token !== "auto")

  const side = OPPOSITE[rawSide] ? rawSide : "bottom"
  const allowed = ALIGNS_FOR[VERTICAL_SIDES[side] ? "vertical" : "horizontal"]
  const align = allowed[rawAlign] ? rawAlign : (VERTICAL_SIDES[side] ? "left" : "top")

  return {side, align, auto}
}

function roomAround(anchor, limit) {
  return {
    top: anchor.top - limit.top,
    bottom: limit.bottom - anchor.bottom,
    left: anchor.left - limit.left,
    right: limit.right - anchor.right
  }
}

function resolveSide(side, anchor, {width, height, gap, margin, limit}) {
  const room = roomAround(anchor, limit)
  const needed = (VERTICAL_SIDES[side] ? height : width) + gap + margin

  if (room[side] >= needed) return side

  const other = OPPOSITE[side]
  return room[other] > room[side] ? other : side
}

// Alignment is measured from the anchor edge the panel starts at, not from the gap
// between anchor and viewport: a bottom-left panel runs rightward from anchor.left, so
// what it needs is the distance from there to the right edge of the window.
function resolveAlign(side, align, anchor, {width, height, margin, limit}) {
  const extent = VERTICAL_SIDES[side]
    ? {left: limit.right - anchor.left, right: anchor.right - limit.left}
    : {top: limit.bottom - anchor.top, bottom: anchor.bottom - limit.top}

  const needed = (VERTICAL_SIDES[side] ? width : height) + margin
  if (extent[align] >= needed) return align

  const other = OPPOSITE[align]
  return extent[other] > extent[align] ? other : align
}

function vertical(side, align, anchor, {width, height, gap, margin, limit}) {
  const top = side === "top"
    ? Math.max(limit.top + margin, anchor.top - height - gap)
    : anchor.bottom + gap

  const left = align === "right" ? anchor.right - width : anchor.left
  const maxLeft = limit.right - width - margin

  return {top, left: Math.max(limit.left + margin, Math.min(left, maxLeft))}
}

function horizontal(side, align, anchor, {width, height, gap, margin, limit}) {
  const left = side === "left"
    ? Math.max(limit.left + margin, anchor.left - width - gap)
    : anchor.right + gap

  const top = align === "bottom" ? anchor.bottom - height : anchor.top
  const maxTop = limit.bottom - height - margin

  return {top: Math.max(limit.top + margin, Math.min(top, maxTop)), left}
}
